import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { GET, POST, PUT } from "../Generic/CrudRequest";
import Icons from "../../Utils/Icons";
import { ClientEdit, ClientAdd } from "./ClientAddOrEdit";

export default function AddOrEditClient({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [title, setTitle] = useState(t("clients.add"));
  const [clientData, setClientData] = useState(new ClientAdd());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location.pathname.includes("/Clientes/Editar")) {
      setTitle(t("clients.edit"));
      const clientId = getClientIdFromPath(location.pathname);
      getClientById(clientId);
    }
  }, [location.pathname]);

  const getClientIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getClientById = async (clientId) => {
    const dataClient = await GET(`${config.url}/api/Client/${clientId}`);
    const updateDataClient = new ClientEdit(dataClient);

    setClientData(updateDataClient);
  };

  const addClient = async () => {
    const requiredFields = ["nombre", "codigo"];
    const newErrors = {};

    if (clientData.correo.trim() !== "" && !validateEmail(clientData.correo)) {
      newErrors.correo = t("clients.invalidEmail");
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(clientData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("clients.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await POST(`${config.url}/api/Client`, clientData);
    navigate("/Clientes");
  };

  const editClient = async () => {
    const requiredFields = ["nombre", "codigo"];
    const newErrors = {};

    if (clientData.correo.trim() !== "" && !validateEmail(clientData.correo)) {
      newErrors.correo = t("clients.invalidEmail");
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(clientData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("clients.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await PUT(`${config.url}/api/Client`, clientData);
    navigate("/Clientes");
  };

  const getFieldByPath = (object, path) => {
    const pathArray = path.split(".");
    let value = object;
    for (const prop of pathArray) {
      value = value[prop];
      if (value === undefined) break;
    }
    return value;
  };

  const handleSubmit = async () => {
    if (title === t("clients.add")) {
      await addClient();
    } else if (title === t("clients.edit")) {
      await editClient();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData({
      ...clientData,
      [name]: value,
    });

    if (name === "correo" && value.trim() !== "") {
      if (!validateEmail(value)) {
        setErrors({
          ...errors,
          [name]: t("clients.invalidEmail"),
        });
      } else {
        setErrors({
          ...errors,
          [name]: "",
        });
      }
    }
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/Clientes"
            tooltip={t("buttons.back")}
          />
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {title} {t("clients.title")}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <CardContent sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }}>
          <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "100%",
              margin: "auto",
            }}
          >
            <AccordionSummary
              sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }}
            >
              <Typography
                variant="h5"
                sx={{ color: theme.palette.primary.main }}
              >
                {t("clients.title")}
              </Typography>
            </AccordionSummary>
            <Divider sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }} />
            <AccordionDetails
              sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label={t("clients.name")}
                      id="Nombre"
                      name="nombre"
                      value={clientData.nombre}
                      variant="standard"
                      onChange={handleChange}
                      error={!!errors.nombre}
                      helperText={errors.nombre}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label={t("clients.code")}
                      id="Codigo"
                      name="codigo"
                      value={clientData.codigo}
                      variant="standard"
                      onChange={handleChange}
                      error={!!errors.codigo}
                      helperText={errors.codigo}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label={t("clients.shortName")}
                      id="NombreCorto"
                      name="nombreCorto"
                      value={clientData.nombreCorto}
                      variant="standard"
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label={t("clients.rfc")}
                      id="RFC"
                      name="rfc"
                      value={clientData.rfc}
                      variant="standard"
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </AccordionDetails>
          </Accordion>
        </CardContent>
        <Card
          variant="outlined"
          sx={{
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
          }}
        >
          <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
            <Typography
              variant="h5"
              sx={{
                flex: "1",
                color: theme.palette.primary.main,
              }}
            >
              {t("clients.clientInfo")}
            </Typography>
            <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("clients.city")}
                  name="ciudad"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.ciudad ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("clients.street")}
                  name="calle"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.calle ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("clients.neighborhood")}
                  name="colonia"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.colonia ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("clients.postalCode")}
                  name="codigoPostal"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.codigoPostal ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("clients.exteriorNumber")}
                  name="noExterior"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.noExterior ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="correo"
                  name="correo"
                  label={t("clients.email")}
                  variant="standard"
                  onChange={handleChange}
                  value={clientData.correo}
                  error={!!errors.correo}
                  helperText={errors.correo}
                />
              </Grid>
            </Grid>

          </CardContent>

        </Card>

        <Card
          variant="outlined"
          sx={{
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            maxWidth: "calc(100% - 32px)",
            margin: "20px auto",
          }}
        >
          <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
            <Typography
              variant="h5"
              sx={{
                flex: "1",
                color: theme.palette.primary.main,
              }}
            >
              {t("clients.configuration")}
            </Typography>
            <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("clients.user")}
                  name="usuario"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.usuario ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("clients.password")}
                  name="contrasena"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.contrasena ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t("clients.url")}
                  name="url"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={clientData.url ?? ""}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                {t("clients.save")}
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}
